import React, { useEffect, useMemo, useRef, useState } from "react";
import { ImageData } from "../../../shared/types/gallery";
import ImageGrid from "../../../shared/components/ui/media/ImageGrid";
import { RefreshCw, ChevronRight, CheckCircle } from "lucide-react";

interface ImageModerationGridProps {
  images: ImageData[];
  onToggle: (id: number) => void;

  // サーバ側ページング/追加ロード用（ページ方式では通常非表示）
  hasNextPage: boolean;
  isLoadingMore: boolean;
  onLoadMore: () => void;
  loading: boolean;
  isRequestActive: boolean;

  // レイアウト
  layout?: "masonry" | "rows";
  targetRowHeight?: number;
  spacing?: number;

  // ★追加: 行ベースのページング
  page?: number;                       // 1-based
  rowsPerPage?: number;                // 1ページに何行表示するか
  onRowsChange?: (totalRows: number) => void; // 総行数を親に通知（totalPages算出用）
  paginateByRows?: boolean;            // true: ページ方式, false: もっと見る方式
}

const usePhotoMeta = (images: ImageData[]) => {
  const [ratios, setRatios] = useState<Record<number, number>>({});
  useEffect(() => {
    let cancelled = false;
    const work = async () => {
      const tasks = images.map(
        (img) =>
          new Promise<void>((resolve) => {
            const el = new Image();
            el.onload = () => {
              if (!cancelled) {
                setRatios((prev) => ({
                  ...prev,
                  [img.id]: (el.naturalWidth || 1) / (el.naturalHeight || 1),
                }));
              }
              resolve();
            };
            el.onerror = () => resolve();
            el.src = (img as any).imageUrl || `data:image/png;base64,${img.base64}`;
          })
      );
      await Promise.all(tasks);
    };
    work();
    return () => {
      cancelled = true;
    };
  }, [images]);
  return ratios;
};

type BuiltRow = { items: ImageData[]; height: number; widths: number[] };

const JustifiedRows: React.FC<{
  images: ImageData[];
  onToggle: (id: number) => void;
  targetRowHeight?: number;
  spacing?: number;

  // ★行ベースページング
  page?: number; // 1-based
  rowsPerPage?: number;
  onLayout?: (info: { totalRows: number }) => void;
}> = ({
  images,
  onToggle,
  targetRowHeight = 220,
  spacing = 12,
  page = 1,
  rowsPerPage = 6,
  onLayout,
}) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [containerWidth, setContainerWidth] = useState<number>(0);
    const ratios = usePhotoMeta(images);

    // しきい値：これを超える超横長は contain 表示にする
    const MAX_ASPECT_RATIO = 2; // お好みで 3.5〜6 くらいに
    const getRawR = (id: number) => ratios[id] || 1;
    const getClampedR = (id: number) => Math.min(getRawR(id), MAX_ASPECT_RATIO);

    useEffect(() => {
      const el = containerRef.current;
      if (!el) return;
      const ro = new ResizeObserver(() => setContainerWidth(el.clientWidth));
      ro.observe(el);
      setContainerWidth(el.clientWidth);
      return () => ro.disconnect();
    }, []);

    const allRows: BuiltRow[] = useMemo(() => {
      if (!containerWidth || images.length === 0) return [];

      // ratios が読み込まれるまで待つ（未取得時はフォールバック: 正方形）
      const hasRatios = Object.keys(ratios).length > 0 || images.length === 0;
      if (!hasRatios) {
        return images.map((img) => ({
          items: [img],
          height: targetRowHeight,
          widths: [targetRowHeight], // 正方形扱い
        }));
      }

      const rows: BuiltRow[] = [];
      let rowItems: ImageData[] = [];
      let aspectSum = 0;

      const flush = (isLast = false) => {
        if (rowItems.length === 0) return;

        const usable = containerWidth - spacing * (rowItems.length - 1);
        let rowH = targetRowHeight;

        if (aspectSum > 0) {
          const fitH = usable / aspectSum; // ぴったり収まる高さ
          if (!isLast) {
            rowH = fitH; // 通常行は常にフィット
          } else {
            // 最終行はオーバーする場合のみ縮める（小さいのは無理に拡大しない）
            const wouldWidth = targetRowHeight * aspectSum;
            if (wouldWidth > usable) rowH = fitH;
          }
        }

        const ws = rowItems.map((it) => rowH * getClampedR(it.id));
        rows.push({ items: rowItems, height: rowH, widths: ws });
        rowItems = [];
        aspectSum = 0;
      };

      for (const img of images) {
        const r = getClampedR(img.id); // 行割当はクリップ済み比率で安定させる
        rowItems.push(img);
        aspectSum += r;
        const used = targetRowHeight * aspectSum + spacing * (rowItems.length - 1);
        if (used >= containerWidth) flush(false);
      }
      flush(true);

      return rows;
    }, [containerWidth, images, spacing, targetRowHeight, ratios]);

    // 親へ総行数を通知（ページ総数計算用）
    useEffect(() => {
      onLayout?.({ totalRows: allRows.length });
    }, [allRows.length, onLayout]);

    // ページに対応する行だけ描画
    const startRow = Math.max(0, (page - 1) * rowsPerPage);
    const endRow = startRow + rowsPerPage;
    const pageRows = allRows.slice(startRow, endRow);

    return (
      <div ref={containerRef} style={{ width: "100%" }}>
        {pageRows.map((row, i) => (
          <div
            key={startRow + i}
            className="justified-row"
            style={{
              display: "flex",
              gap: `${spacing}px`,
              marginBottom: `${spacing}px`,
              alignItems: "stretch",
            }}
          >
            {row.items.map((img, idx) => {
              const src = (img as any).imageUrl || `data:image/png;base64,${img.base64}`;

              const statusClass =
                img.isModerated === 1
                  ? "approved"
                  : img.isModerated === 2
                    ? "rejected"
                    : "unmoderated";

              const isUltraWide = getRawR(img.id) > MAX_ASPECT_RATIO; // 超横長なら contain

              return (
                <div
                  key={img.id}
                  className={`elegant-image-item ${statusClass} ${img.selected ? "selected" : ""}`}
                  style={{
                    width: `${row.widths[idx]}px`,
                    height: `${row.height}px`,
                    position: "relative",
                    overflow: "visible",
                    borderRadius: 12,
                    cursor: "pointer",
                  }}
                  onClick={() => onToggle(img.id)}
                >
                  <div
                    className="image-container"
                    style={{
                      position: "relative",
                      width: "100%",
                      height: "100%",
                      overflow: "hidden",
                      borderRadius: 12,
                      background: isUltraWide ? "#f3f4f6" : undefined,
                    }}
                  >
                    <img
                      src={src}
                      alt={`img-${img.id}`}
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: isUltraWide ? "contain" : "cover",
                        objectPosition: "center",
                        borderRadius: 12,
                        transition: "transform 0.3s ease",
                      }}
                    />
                    <div className="image-overlay">
                      <div className="overlay-content">
                        {img.selected && (
                          <div className="selection-indicator">
                            <CheckCircle size={20} className="check-icon" />
                            <span className="selection-text">選択中</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="status-badge">
                      {img.isModerated === 1 && (
                        <span className="badge approved-badge">承認済み</span>
                      )}
                      {img.isModerated === 2 && (
                        <span className="badge rejected-badge">拒否済み</span>
                      )}
                      {img.isModerated === 0 && (
                        <span className="badge unmoderated-badge">未検閲</span>
                      )}
                    </div>
                    <div className="decorative-border" />
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    );
  };

const ImageModerationGrid: React.FC<ImageModerationGridProps> = ({
  images,
  onToggle,
  hasNextPage,
  isLoadingMore,
  onLoadMore,
  loading,
  isRequestActive,
  layout = "masonry",
  targetRowHeight = 220,
  spacing = 12,

  // ★追加: ページ方式のための props
  page = 1,
  rowsPerPage = 6,
  onRowsChange,
  paginateByRows = true, // デフォルトでページ方式
}) => {
  const showLoadMore = !paginateByRows && hasNextPage;

  return (
    <div className="elegant-image-grid-container">
      {layout === "masonry" ? (
        <ImageGrid images={images} onToggle={onToggle} useMasonry={true} />
      ) : (
        <JustifiedRows
          images={images}
          onToggle={onToggle}
          targetRowHeight={targetRowHeight}
          spacing={spacing}
          page={page}
          rowsPerPage={rowsPerPage}
          onLayout={(info) => onRowsChange?.(info.totalRows)}
        />
      )}

      {showLoadMore && (
        <div
          className="load-more-container"
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "12px",
            margin: "32px 0",
          }}
        >
          <button
            className={`pagination-button next ${isLoadingMore || loading ? "loading" : ""}`}
            onClick={onLoadMore}
            disabled={isLoadingMore || loading || isRequestActive}
            style={{
              padding: "12px 24px",
              borderRadius: "8px",
              border: "none",
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "white",
              cursor: isLoadingMore ? "not-allowed" : "pointer",
              opacity: isLoadingMore ? 0.7 : 1,
            }}
          >
            {isLoadingMore || loading ? (
              <>
                <RefreshCw size={16} className="spinning" style={{ marginRight: "8px" }} />
                読み込み中...
              </>
            ) : (
              <>
                <ChevronRight size={16} style={{ marginRight: "8px" }} />
                もっと見る
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default ImageModerationGrid;
